import { Fragment, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import type { Deployment, DeploymentTarget } from '../lib/api';
import {
  DeploymentStatusBadge,
  Empty,
  ErrorNotice,
  Loading,
  Notice,
  OnlineBadge,
  Panel,
  formatBytes,
  formatDateTime,
  relativeTime,
  useAsync,
} from '../components/ui';

interface Detail {
  deployment: Deployment & {
    apk_version_id: string;
    sha256: string;
    min_sdk_version: number | null;
    file_size_bytes: number;
  };
  targets: DeploymentTarget[];
}

interface TimelineEvent {
  from_status: string | null;
  to_status: string;
  source: string;
  message: string | null;
  created_at: string;
}

export default function DeploymentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Polls: the device side is poll-based, so progress arrives asynchronously.
  const { data, error, loading, reload } = useAsync(
    (signal) => api.get<Detail>(`/deployments/${id}`, signal),
    [id],
    5_000,
  );

  const timeline = useAsync(
    (signal) =>
      expanded
        ? api.get<{ events: TimelineEvent[] }>(`/deployment-targets/${expanded}/timeline`, signal)
        : Promise.resolve({ events: [] }),
    [expanded],
  );

  const act = async (path: string, confirmText?: string) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setActionError(null);
    setMessage(null);
    try {
      const result = await api.post<{ message: string }>(path);
      setMessage(result.message);
      reload();
    } catch (err) {
      setActionError(err);
    }
  };

  if (loading && !data) return <Loading what="deployment" />;
  if (error) return <ErrorNotice error={error} />;
  if (!data) return <Empty>Deployment not found.</Empty>;

  const d = data.deployment;
  const inFlight = data.targets.filter((t) =>
    ['pending', 'awaiting_device', 'notified', 'downloading', 'installing'].includes(t.status),
  );
  const failed = data.targets.filter((t) => t.status === 'failed');
  const retryable = failed.filter(
    (t) => !['INCOMPATIBLE_SDK', 'NOT_ENROLLED'].includes(t.last_error_code ?? ''),
  );

  return (
    <>
      <div className="page-head">
        <div>
          <h2>{d.application_name} v{d.version_name}</h2>
          <p className="mono">
            {d.package_name} · versionCode {d.version_code} ·{' '}
            <Link to="/deployments">back to deployments</Link>
          </p>
        </div>
        <div className="actions">
          <DeploymentStatusBadge status={d.status} />
          {inFlight.length > 0 && (
            <button onClick={() => act(`/deployments/${id}/cancel`, 'Cancel all pending installs? Devices that already installed keep the APK.')}>
              Cancel pending
            </button>
          )}
          {retryable.length > 0 && (
            <button className="primary" onClick={() => act(`/deployments/${id}/retry`)}>
              Retry {retryable.length} failed
            </button>
          )}
        </div>
      </div>

      {message && <Notice kind="info">{message}</Notice>}
      <ErrorNotice error={actionError} />

      <div className="grid cols-4" style={{ marginBottom: 16 }}>
        <div className="stat">
          <div className="value">{d.target_count}</div>
          <div className="label">Targets</div>
        </div>
        <div className="stat">
          <div className="value" style={{ color: 'var(--ok)' }}>{d.success_count}</div>
          <div className="label">Installed (confirmed)</div>
        </div>
        <div className="stat">
          <div className="value" style={{ color: d.failure_count ? 'var(--danger)' : undefined }}>{d.failure_count}</div>
          <div className="label">Failed</div>
        </div>
        <div className="stat">
          <div className="value" style={{ color: inFlight.length ? 'var(--warn)' : undefined }}>{inFlight.length}</div>
          <div className="label">In flight</div>
        </div>
      </div>

      {inFlight.length > 0 && (
        <Notice kind="info">
          {inFlight.filter((t) => t.status === 'awaiting_device').length > 0 ? (
            <>
              {inFlight.filter((t) => t.status === 'awaiting_device').length} device(s) are offline.
              The update is queued and will be applied when they reconnect — nothing is lost.
            </>
          ) : (
            <>This page refreshes every 5 seconds as devices report progress.</>
          )}
        </Notice>
      )}

      <Panel title="Build">
        <dl className="kv">
          <dt>SHA-256</dt><dd className="mono small">{d.sha256}</dd>
          <dt>Size</dt><dd>{formatBytes(d.file_size_bytes)}</dd>
          <dt>Min SDK</dt><dd>{d.min_sdk_version ?? '—'}</dd>
          <dt>Started</dt><dd>{formatDateTime(d.started_at)}</dd>
          <dt>Completed</dt><dd>{formatDateTime(d.completed_at)}</dd>
          <dt>Created by</dt><dd>{d.created_by_email ?? '—'}</dd>
        </dl>
        <p className="small muted" style={{ marginBottom: 0 }}>
          The device verifies this checksum against the file it downloads before installing.
        </p>
      </Panel>

      <Panel title="Per-device status">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Device</th><th>Status</th><th>Detail</th>
                <th className="num">Attempts</th><th>Connection</th><th>Updated</th><th />
              </tr>
            </thead>
            <tbody>
              {data.targets.map((t) => (
                <Fragment key={t.id}>
                  <tr>
                    <td>
                      <Link to={`/devices/${t.device_id}`}>{t.device_name}</Link>
                      <div className="small muted">{t.model} · Android {t.android_version}</div>
                    </td>
                    <td><DeploymentStatusBadge status={t.status} /></td>
                    <td className="small">
                      {t.status_message ?? '—'}
                      {t.last_error_code && (
                        <div className="mono small" style={{ color: 'var(--danger)' }}>{t.last_error_code}</div>
                      )}
                    </td>
                    <td className="num">{t.attempts}</td>
                    <td><OnlineBadge state={t.deviceOnline} /></td>
                    <td className="small muted">{relativeTime(t.completed_at ?? t.notified_at)}</td>
                    <td>
                      <button
                        className="small"
                        onClick={() => setExpanded(expanded === t.id ? null : t.id)}
                      >
                        {expanded === t.id ? 'Hide' : 'Timeline'}
                      </button>
                    </td>
                  </tr>
                  {expanded === t.id && (
                    <tr>
                      <td colSpan={7} style={{ background: 'var(--bg)' }}>
                        {timeline.loading ? (
                          <Loading what="timeline" />
                        ) : (
                          <ul className="timeline">
                            {(timeline.data?.events ?? []).map((e, i) => (
                              <li key={i}>
                                <time>{formatDateTime(e.created_at)}</time>
                                <span>
                                  <strong>{e.from_status ?? 'created'} → {e.to_status}</strong>
                                  <span className="muted small"> [{e.source}]</span>
                                  {e.message && <div className="small">{e.message}</div>}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
}
